import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo, useRef, useLayoutEffect } from 'react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, useTheme, jsx } from '@emotion/react';
import { utilStyles, AnimateDirection, KeyboardCode } from '@paypalcorp/pp-react-utils';
import { textStyles } from '@paypalcorp/pp-react-text';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7;

function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive,
      sysColorBackgroundAlt = tokens.sysColorBackgroundAlt,
      sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysColorPrimaryContrast = tokens.sysColorPrimaryContrast,
      sysColorPrimaryMain = tokens.sysColorPrimaryMain,
      sysColorStructureFocus = tokens.sysColorStructureFocus,
      sysMotionDurationAction = tokens.sysMotionDurationAction,
      sysMotionDurationHover = tokens.sysMotionDurationHover,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceTiny = tokens.sysSpaceTiny;

  var _utilStyles = utilStyles(tokens),
      focusStyles = _utilStyles.focusStyles,
      slideTo = _utilStyles.slideTo,
      variables = _utilStyles.variables;

  var ppuiEaseInOut = variables.ppuiEaseInOut;

  var _textStyles = textStyles(tokens),
      textCaption = _textStyles.caption;
  /* istanbul ignore next */


  var borderRadius = sysBorderRadiusInteractive === '50%' ? '64rem' : sysBorderRadiusInteractive;
  /**
   * Use direction to do slide animation
   * @param direction AnimateDirection
   * @returns SerializedStyles
   */

  var animateToDirection = function animateToDirection(direction) {
    var label = "animate_direction_".concat(direction);
    return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n      label: ", ";\n      animation: ", " ", "\n        ", ";\n    "])), label, slideTo(direction), sysMotionDurationAction, ppuiEaseInOut);
  };

  var tabWrapBaseStyles = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: segmented_controller_tab_wrap_base;\n    background-color: ", ";\n    border-radius: ", ";\n    display: flex;\n    align-items: stretch;\n    justify-content: flex-start;\n  "])), sysColorBackgroundAlt, borderRadius);
  var tabStyles = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: segmented_controller_tab;\n    border-radius: inherit;\n    position: relative;\n    background-color: transparent;\n    width: 100%;\n    border: 0.0625rem solid transparent;\n    padding: ", ";\n    min-width: 0;\n    cursor: pointer;\n    ", "\n\n    &:focus {\n      outline: none;\n      > span:first-of-type {\n        ", ";\n      }\n    }\n\n    &:active {\n      outline: none;\n      &:after {\n        border: none;\n        box-shadow: none;\n      }\n    }\n    @media (hover: hover) {\n      &:hover {\n        &:not(:focus) {\n          span {\n            border-color: ", ";\n            transition: border-color ", " ", ";\n          }\n        }\n      }\n    }\n\n    &[aria-selected='true'] {\n      label: tab_aria_selected;\n      ", "\n      -webkit-text-stroke-width: ", ";\n\n      span {\n        color: ", ";\n        background-color: ", ";\n      }\n    }\n  "])), sysSpaceTiny, textCaption(), focusStyles.focusOffsetElement, sysColorStructureFocus, sysMotionDurationHover, ppuiEaseInOut, textCaption(), '0.0421875rem', sysColorPrimaryMain, sysColorPrimaryContrast);
  var tabTitleStyles = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: tab_title_base;\n    display: flex;\n    flex-wrap: wrap;\n    justify-content: center;\n    align-items: center;\n    height: 100%;\n    word-break: break-word;\n    border: 0.0625rem solid transparent;\n    padding: ", ";\n    border-radius: inherit;\n    color: ", ";\n    pointer-events: none;\n  "])), sysSpaceSm, sysColorBackgroundMediumContrast);
  var tabContentStyles = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: tab_content;\n\n    &[aria-hidden='true'] {\n      display: none;\n    }\n  "])));
  var tabContentVisible = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: tab_content_visible;\n    display: block;\n  "])));
  var tabContentHidden = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: tab_content_hidden;\n    display: none;\n  "])));
  return {
    animateToDirection: animateToDirection,
    tabWrapBaseStyles: tabWrapBaseStyles,
    tabStyles: tabStyles,
    tabTitleStyles: tabTitleStyles,
    tabContentStyles: tabContentStyles,
    tabContentVisible: tabContentVisible,
    tabContentHidden: tabContentHidden
  };
}

var _excluded = ["id", "tabIdx", "label", "segmentedControllerId", "panelId", "selected", "animate", "onClick", "className"];

var SegmentedControllerTab = function SegmentedControllerTab(_ref) {
  var _styles$animateToDire;

  var id = _ref.id,
      tabIdx = _ref.tabIdx,
      label = _ref.label,
      segmentedControllerId = _ref.segmentedControllerId,
      panelId = _ref.panelId,
      selected = _ref.selected,
      animate = _ref.animate,
      onClick = _ref.onClick,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, _excluded);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var focusable = selected ? 0 : -1;
  var tabId = id || "".concat(segmentedControllerId, "-tab").concat(parseInt(tabIdx, 10) + 1);
  var tabStyles = [_styles.tabStyles, animate && ((_styles$animateToDire = _styles.animateToDirection) === null || _styles$animateToDire === void 0 ? void 0 : _styles$animateToDire.call(_styles, animate))];
  return jsx("button", _extends({
    "data-tab-index": tabIdx,
    id: tabId,
    css: tabStyles,
    className: className,
    "aria-controls": panelId,
    "aria-selected": selected,
    tabIndex: focusable,
    type: "button",
    role: "tab"
  }, rest, {
    onClick: onClick,
    "data-ppui": "true"
  }), jsx("span", {
    css: _styles.tabTitleStyles,
    "data-ppui": "true"
  }, label));
};

var _excluded$1 = ["id", "panelId", "label"];

var getRefCurrent = function getRefCurrent(myRef) {
  return (
    /* istanbul ignore next */
    myRef && myRef.current ? myRef.current : null
  );
};

var SegmentedControllerTabs = function SegmentedControllerTabs(_ref) {
  var _ref$name = _ref.name,
      name = _ref$name === void 0 ? 'segmentedController' : _ref$name,
      selectedTabIdx = _ref.selectedTabIdx,
      _ref$tabs = _ref.tabs,
      tabs = _ref$tabs === void 0 ? [] : _ref$tabs,
      onClick = _ref.onClick,
      className = _ref.className,
      tabClassName = _ref.tabClassName,
      tabsWrapperClassName = _ref.tabsWrapperClassName,
      keepAlive = _ref.keepAlive,
      lazyLoad = _ref.lazyLoad,
      children = _ref.children;
  /* istanbul ignore next */

  if (!onClick) {
    console.warn('Required prop `onClick` is missing');
  }

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var containerEleRef = useRef();
  var prevSelectedTabIdxRef = useRef(0);
  var isTabsFocusedRef = useRef(false);
  var focusedIdxRef = useRef();

  var getSelectedTabIdx = function getSelectedTabIdx() {
    var index = Math.floor(selectedTabIdx);

    if (!selectedTabIdx || !index || index >= tabs.length || index < 0) {
      return 0;
    }

    return index;
  };

  var offsetFocus = function offsetFocus(offset) {
    var container = getRefCurrent(containerEleRef);
    var tabDomItems = container.querySelectorAll('[role="tab"]');
    var nextIndex = focusedIdxRef.current + offset;
    var index;
    /* istanbul ignore next */

    if (nextIndex === tabs.length) {
      index = 0;
    } else if (nextIndex === -1) {
      index = tabs.length - 1;
    } else {
      index = nextIndex;
    }

    tabDomItems[index].focus();
    focusedIdxRef.current = index;
  };

  var keyHandler = function keyHandler(event) {
    if (!isTabsFocusedRef.current) {
      return;
    }

    var selectedIndex = getSelectedTabIdx();
    /* istanbul ignore next */

    var KeyCode = new KeyboardCode(event.key || event.keyCode);

    if (KeyCode.IS_ARROW_LEFT) {
      offsetFocus(-1);
    } else if (KeyCode.IS_ARROW_RIGHT) {
      offsetFocus(1);
    } else if (KeyCode.IS_ENTER || KeyCode.IS_SPACEBAR) {
      /* istanbul ignore next */
      if (selectedIndex !== focusedIdxRef.current) {
        onClick(event, focusedIdxRef.current);
      }
    }
  };

  useLayoutEffect(function () {
    prevSelectedTabIdxRef.current = getSelectedTabIdx();
  }, [tabs, selectedTabIdx, onClick]);
  useLayoutEffect(function () {
    // For the first time during initialisation
    focusedIdxRef.current = getSelectedTabIdx();
  }, []);

  var handleClick = function handleClick(event) {
    var tabIdxClicked = parseInt(event.currentTarget.getAttribute('data-tab-index'), 10);
    event.currentTarget.focus(); // This is tested, but coverage not registering it

    /* istanbul ignore next */

    if (tabIdxClicked) {
      focusedIdxRef.current = tabIdxClicked;
    }

    onClick(event, tabIdxClicked);
  };

  var selectedIndex = getSelectedTabIdx();
  var selectedId;
  var tabItems = tabs.map(function (tabProps, index) {
    var id = tabProps.id,
        panelId = tabProps.panelId,
        label = tabProps.label,
        rest = _objectWithoutProperties(tabProps, _excluded$1);

    var keyIdentifier = "".concat(name, "-tab").concat(index + 1);
    var animateDirection = null; // update animation (only on the selected tab)

    if (index === selectedIndex) {
      selectedId = id || keyIdentifier;

      if (prevSelectedTabIdxRef.current > index) {
        animateDirection = AnimateDirection.LEFT;
      } else if (prevSelectedTabIdxRef.current < index) {
        animateDirection = AnimateDirection.RIGHT;
      }
    }

    return jsx(SegmentedControllerTab, _extends({
      key: keyIdentifier,
      id: id,
      tabIdx: index.toString(),
      label: label,
      segmentedControllerId: name,
      panelId: panelId,
      selected: index === selectedIndex,
      animate: animateDirection,
      onClick: handleClick,
      className: tabClassName
    }, rest, {
      "data-ppui": "true"
    }));
  });
  var tabContent = null;

  if (keepAlive || lazyLoad) {
    tabContent = React.Children.toArray(children).map(function (_panel, index) {
      var isSelected = selectedIndex === index;
      return React.cloneElement(_panel, {
        hidden: !isSelected,
        tabId: isSelected ? selectedId : undefined,
        visible: keepAlive ? true : isSelected
      });
    });
  } else {
    var tabPanel = React.Children.toArray(children).filter(function (_panel, index) {
      return selectedIndex === index;
    })[0];
    tabContent = React.cloneElement(tabPanel, {
      tabId: selectedId,
      hidden: false
    });
  }

  return jsx("div", {
    ref: containerEleRef,
    id: name,
    className: className,
    "data-ppui-info": "segmented-controller_2.7.0"
  }, jsx("div", {
    id: "".concat(name, "-tabs"),
    css: _styles.tabWrapBaseStyles,
    className: tabsWrapperClassName,
    role: "tablist",
    onFocus: function handleFocus() {
      isTabsFocusedRef.current = true;
    },
    onBlur: function handleFocusOut() {
      isTabsFocusedRef.current = false;
    },
    onKeyDown: function onKeyDown(evt) {
      return keyHandler(evt);
    },
    "data-ppui": "true"
  }, tabItems), tabContent);
};

var _excluded$2 = ["id", "tabId", "className", "hidden", "children", "visible"];

var SegmentedControllerPanel = function SegmentedControllerPanel(_ref) {
  var id = _ref.id,
      tabId = _ref.tabId,
      className = _ref.className,
      hidden = _ref.hidden,
      children = _ref.children,
      _ref$visible = _ref.visible,
      visible = _ref$visible === void 0 ? true : _ref$visible,
      rest = _objectWithoutProperties(_ref, _excluded$2);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]); // MutableRefObject<boolean> variable to determine whether to render the component or not.


  var hasRendered = useRef(visible);
  /* istanbul ignore next */

  if (visible && !hasRendered.current) {
    hasRendered.current = true;
  }
  /* istanbul ignore next */


  if (!hasRendered.current) {
    // Do not re-render the Panel when its already present in the DOM.
    return null;
  }

  var panelContentStyles = [_styles.tabContentStyles,
  /* istanbul ignore next */
  visible ? _styles.tabContentVisible : _styles.tabContentHidden];
  return jsx("div", _extends({
    id: id,
    css: panelContentStyles,
    className: className,
    "aria-labelledby": tabId,
    "aria-hidden": hidden,
    tabIndex: 0,
    role: "tabpanel"
  }, rest, {
    "data-ppui": "true"
  }), children);
};

export { SegmentedControllerTabs as SegmentedController, SegmentedControllerPanel, SegmentedControllerTab, styles as segmentedControllerStyles };